
import { Component, Mixins } from "vue-property-decorator";
import { without, cloneDeep } from "lodash";
import format from "date-fns/format";
import { v4 as uuidv4 } from "uuid";
import {
  SalesPackage,
  CartPackages,
  NullPackage,
  BackendCartRequest
} from "@/interfaces/packages/sales_packages";
import SnackbarMixin from "@/mixins/SnackbarMixin.vue";
import { updateCart } from "@/services/api/cart";
import { AxiosInstance } from "axios";
import { setClientAuthentication } from "@/services/api/client";
import { JsonConsoleLogger } from "@/services/logging/consoleLogger";
import { getDetails, loadMeetingState } from "@/services/api/meeting";
import { CartResponse } from "@/interfaces/meeting/details";
import { sendUpdateCart } from "@/services/meeting/messaging";
import { convertCart } from "@/utils/cart";

@Component
export default class CartMixin extends Mixins<SnackbarMixin>(SnackbarMixin) {
  dates: string[] = [];
  apiClient!: AxiosInstance;
  showCart = false;
  showErrorDialog = false;
  selectedPackage: SalesPackage = NullPackage;
  packagesInCart: CartPackages[] = [];
  addingToCart = false;
  submitDisabled = true;
  itemClicked = "";
  removingPackage = false;
  previousCart: CartPackages[] = []; // Need this so we can revert when error happens during api call
  comment = "";
  additionalPackageField = "";
  stations: string[] = [];
  errorTitle = "";
  errorDetails = "";
  minimumDate = format(new Date(), "YYYY-MM-DD");
  eventId = 0;
  additionalField = "";
  selectedGiptPackageId = -1;

  async created() {
    const token =
      new URLSearchParams(window.location.search).get("token") || "";
    this.apiClient = setClientAuthentication(token);
    const serverDetails = await getDetails(this.apiClient);
    this.eventId = serverDetails.event_id;
    this.additionalField = serverDetails.additional_field;
  }

  setPackage(selectedPackage: SalesPackage) {
    this.selectedPackage = selectedPackage;
    this.validateCart();
  }

  findPackage(cartPackage: CartPackages) {
    return this.packagesInCart.find(item => {
      return item.id === cartPackage.id;
    });
  }

  setDates(value: string) {
    const dateExist = this.dates.includes(value);

    if (!dateExist) {
      this.dates.push(value);
    } else {
      this.dates = this.dates.filter(item => item !== value);
    }
    this.validateCart();
  }

  setPackageComment(value: string) {
    this.comment = value;
    this.validateCart();
  }

  setAdditionalPackageField(value: string) {
    this.additionalPackageField = value;
    this.validateCart();
  }

  setStations(value: string[]) {
    this.stations = value;
    this.validateCart();
  }

  resetStations() {
    this.stations = [];
  }

  updateDates(payload: { value: string; item: CartPackages }) {
    this.submitDisabled = false;
    const itemExist = this.findPackage(payload.item);

    if (itemExist) {
      if (itemExist.dates.includes(payload.value)) {
        itemExist.dates = itemExist.dates.filter(
          date => date !== payload.value
        );
      } else {
        itemExist.dates.push(payload.value);
      }
    }
  }

  async removeFromCart(cartPackage: CartPackages) {
    this.itemClicked = cartPackage.id;
    this.removingPackage = true;
    const cartWithoutPackage = without(this.packagesInCart, cartPackage);
    const cartRequest: BackendCartRequest[] = cartWithoutPackage.map(item => {
      let obj = {};
      if (this.stations.length > 0) {
        obj = {
          station1: this.stations[0],
          station2: this.stations[1],
          stations: {
            [this.stations[0]]: "\u2718",
            [this.stations[1]]: "\u2718"
          }
        };
      }
      return {
        packageId: item.package.id,
        packageName: item.package.name,
        packagePrice: item.package.price,
        taxText: item.package.taxText || "YOLO",
        dates: item.dates,
        station1: "",
        station2: "",
        stations: {},
        ...obj
      };
    });
    try {
      await updateCart(this.apiClient, cartRequest);
      this.packagesInCart = cloneDeep(cartWithoutPackage);
      this.previousCart = cloneDeep(cartWithoutPackage);
      this.resetCart();
      this.showSnackbar(this.$t("Successfully updated cart").toString());
      sendUpdateCart();
    } catch (ex) {
      this.handleError();
    } finally {
      this.itemClicked = "";
      this.removingPackage = false;
    }
  }

  validateCart() {
    if (this.eventId == 129) {
      if (this.selectedPackage.id) {
        this.submitDisabled = false;
      } else {
        this.submitDisabled = true;
      }
    } else {
      if (
        this.selectedPackage.id &&
        this.dates.length &&
        this.dates.length <= 10
      ) {
        this.submitDisabled = false;
      } else {
        this.submitDisabled = true;
      }
    }
  }

  handleError() {
    this.errorTitle = this.$t("Failed to update cart").toString();
    this.errorDetails = this.$t("failed_to_update_cart_details").toString();
    this.showErrorDialog = true;
  }

  onFirstLoadCart(existingPackages: CartPackages[]) {
    if (existingPackages.length > 0) {
      this.packagesInCart = cloneDeep(existingPackages);
      this.previousCart = cloneDeep(existingPackages);
    }
  }

  async updateCart() {
    this.addingToCart = true;
    // For event 129, dates is not required
    if (this.hasSelectedPackage && (this.eventId == 129 || this.dates.length)) {
      let obj = {};
      if (this.stations.length > 0) {
        obj = {
          station1: this.stations[0],
          station2: this.stations[1],
          stations: {
            [this.stations[0]]: "\u2718",
            [this.stations[1]]: "\u2718"
          }
        };
      }
      console.log("UPDATING CART", this.selectedPackage);
      const cartObj = {
        id: uuidv4(), // Generating uuid to identify different cart items, easier to update values
        package: this.selectedPackage,
        dates: this.dates,
        comment: this.comment,
        additionalField: this.additionalPackageField,
        station1: "",
        station2: "",
        stations: {},
        taxText: this.selectedPackage.taxText || "YEEHA",
        ...obj
      };
      this.packagesInCart.push(cartObj);
    }

    const cartRequest: BackendCartRequest[] = this.packagesInCart.map(item => {
      debugger;
      return {
        packageId: item.package.id,
        packageName: item.package.name,
        packagePrice: item.package.price,
        packageDescription: item.package.description,
        dates: item.dates,
        comment: item.comment,
        additionalField: item.additionalField,
        station1: item.station1,
        station2: item.station2,
        stations: item.stations,
        taxText: item.package.taxText
      };
    });
    try {
      await updateCart(this.apiClient, cartRequest);
      this.previousCart = cloneDeep(this.packagesInCart);
      this.resetCart();
      this.showSnackbar(this.$t("Successfully updated cart").toString());
      sendUpdateCart();
    } catch (ex) {
      this.packagesInCart = cloneDeep(this.previousCart); // If theres error, then revert to previous values because we do not want the current cart item values to change
      this.handleError();
    } finally {
      this.addingToCart = false;
    }
  }

  async loadCart(logger: JsonConsoleLogger) {
    logger.info("[CART] Cart has been updated, will load cart from server.");
    let { cart } = await loadMeetingState<CartResponse>(this.apiClient, "cart");
    if (!cart) {
      cart = [];
    }
    this.packagesInCart = convertCart(cart);
  }

  resetCart() {
    this.packagesInCart = cloneDeep(this.previousCart); // Revert to previous cart in case values are changed but user clicks cancel
    this.showErrorDialog = false;
    this.selectedPackage = NullPackage;
    this.addingToCart = false;
    this.dates = [];
    this.comment = "";
    this.submitDisabled = true;
    this.selectedGiptPackageId = -1;
  }

  closeCart() {
    this.showCart = false;
    if (this.eventId == 192) {
      (this as any).isDisplayDialog192 = true;
    }
    this.resetCart();
  }

  get hasSelectedPackage() {
    return this.selectedPackage !== (NullPackage || {} || undefined);
  }
}
